import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { Divider, IconView, MenuItem } from '@gem/ui-kit';

import { AvatarTypes } from 'APP/components/Avatar/Avatar';
import { GroupAvatar } from 'APP/components/Avatar/GroupAvatar/GroupAvatar';
import { SecureChatBadge } from 'APP/components/SecureChatBadge/SecureChatBadge';
import { VerifiedBadge } from 'APP/components/VerifiedBadge/VerifiedBadge';

import { GroupUpdateLinkButton } from '../components/GroupUpdateLinkButton/GroupUpdateLinkButton';
import InviteLink from '../components/InviteLink';
import { ProfileDescription } from '../components/ProfileDescription/ProfileDescription';
import { SpaceLink } from '../components/SpaceLink';
import { SpaceVisibility } from '../components/SpaceVisibility/SpaceVisibility';
import { useGroupProfilePresenter } from './GroupProfile.presenter';
import styles from './GroupProfile.styles.m.css';

export const GroupProfile = observer(({ className, groupId, actions }) => {
  const presenter = useGroupProfilePresenter({ groupId, actions });
  const { t } = useTranslation();

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.header}>
        <GroupAvatar
          className={styles.avatar}
          group={presenter.group}
          type={AvatarTypes.Round_20}
          withFullScreen={true}
        />
        <div className={styles.info}>
          <h3 className={styles.title}>
            {presenter.isSecretGroup && <SecureChatBadge className={styles.secureIcon} />}
            {presenter.title} {presenter.verified && <VerifiedBadge className={styles.verified} />}
          </h3>
          <p className={styles.subtitle}>{presenter.subtitle}</p>
          {presenter.showSecretChatSubtitle && (
            <div className={styles.subtitle}>
              {t('secure_chat_end_to_end_encrypted')} ·{' '}
              <button
                onClick={presenter.onLearnMoreClick}
                className={styles.learnMore}
              >
                {t('secure_chat_learn_more')}
              </button>
            </div>
          )}
        </div>
      </div>

      {presenter.description && (
        <ProfileDescription
          className={styles.contact}
          groupId={groupId}
          mentions={presenter.group?.descriptionMentions}
          description={presenter.description}
        />
      )}
      {presenter.isShowInviteLink && (
        <InviteLink
          className={styles.contact}
          deepLink={presenter.group.deepLink}
          Actions={<GroupUpdateLinkButton group={presenter.group} />}
        />
      )}
      {presenter.space && <SpaceLink space={presenter.space}></SpaceLink>}

      {presenter.actions.length > 0 && <Divider theme="style2" />}
      {presenter.actions.map(({ key, title, Icon, onClick }) => (
        <MenuItem
          className={styles.listItem}
          key={key}
          title={title}
          icon={
            <IconView
              Icon={Icon}
              className={styles.listIcon}
            />
          }
          onClick={onClick}
        />
      ))}
      <SpaceVisibility
        className={styles.listItem}
        titleClassName={styles.listItemTitle}
        group={presenter.group}
      />

      {presenter.childPagesList.length > 0 && <Divider theme="style1" />}
      {presenter.childPagesList.map(({ key, title, Icon, onClick, count }) => (
        <MenuItem
          className={styles.listItem}
          key={key}
          title={title}
          count={count}
          icon={
            <IconView
              Icon={Icon}
              className={styles.listIcon}
            />
          }
          onClick={onClick}
        />
      ))}

      <Divider theme="style1" />
      {presenter.actionItems.map(({ key, title, Icon, onClick, value }) => (
        <MenuItem
          value={value}
          className={styles.listItem}
          key={key}
          title={title}
          icon={
            <IconView
              Icon={Icon}
              className={styles.listIcon}
            />
          }
          onClick={onClick}
        />
      ))}
    </div>
  );
});
